/** Parse YouTube time param: 90, 90s, 1h2m3s, 26m37s */
export function parseYoutubeTimeParam(raw) {
  const t = String(raw || "").trim();
  if (!t) return 0;
  if (/^\d+$/i.test(t)) return Math.max(0, parseInt(t, 10));
  if (/^\d+s$/i.test(t)) return Math.max(0, parseInt(t, 10));

  const h = t.match(/(\d+)h/i);
  const m = t.match(/(\d+)m/i);
  const s = t.match(/(\d+)s/i);
  if (h || m || s) {
    const hours = h ? parseInt(h[1], 10) : 0;
    const mins = m ? parseInt(m[1], 10) : 0;
    const secs = s ? parseInt(s[1], 10) : 0;
    return Math.max(0, hours * 3600 + mins * 60 + secs);
  }
  return 0;
}

/**
 * @param {string} input
 * @returns {{ videoId: string, startSec: number } | { error: 'shorts' } | null}
 */
export function parseYoutubeUrl(input) {
  const raw = String(input || "").trim();
  if (!raw) return null;
  if (raw.includes("/shorts/")) return { error: "shorts" };

  try {
    const url = new URL(raw);
    const host = url.hostname.replace(/^www\./, "").toLowerCase();
    let videoId = null;

    if (host === "youtu.be") {
      videoId = url.pathname.replace(/^\//, "").split("/")[0];
    } else if (host === "youtube.com" || host === "m.youtube.com" || host.endsWith(".youtube.com")) {
      if (url.pathname === "/watch") {
        videoId = url.searchParams.get("v");
      } else if (url.pathname.startsWith("/embed/")) {
        videoId = url.pathname.split("/")[2];
      } else if (url.pathname.startsWith("/v/")) {
        videoId = url.pathname.split("/")[2];
      }
    }

    if (!videoId || !/^[\w-]{11}$/.test(videoId)) return null;

    const fromQuery =
      parseYoutubeTimeParam(url.searchParams.get("t")) ||
      parseYoutubeTimeParam(url.searchParams.get("start"));
    return { videoId, startSec: fromQuery };
  } catch {
    return null;
  }
}

export function isHostedVideoUrl(input) {
  const raw = String(input || "").trim();
  if (!/^https:\/\//i.test(raw)) return false;
  try {
    const url = new URL(raw);
    return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url.pathname) || url.pathname.length > 1;
  } catch {
    return false;
  }
}

/**
 * @param {'youtube'|'file'} sourceType
 * @param {string} url
 * @param {number} startSec
 */
export function buildPreviewModel(sourceType, url, startSec = 0) {
  const trimmed = String(url || "").trim();
  if (!trimmed) return { kind: "empty" };

  if (sourceType === "youtube") {
    const parsed = parseYoutubeUrl(trimmed);
    if (!parsed) return { kind: "invalid", message: "Enter a valid YouTube watch URL." };
    if (parsed.error === "shorts") {
      return { kind: "invalid", message: "YouTube Shorts URLs are not supported for preview or clipping." };
    }
    const start = Math.max(0, Number(startSec) || parsed.startSec || 0);
    const embedUrl = `https://www.youtube-nocookie.com/embed/${parsed.videoId}?start=${Math.floor(start)}&rel=0&modestbranding=1&playsinline=1`;
    return { kind: "youtube", videoId: parsed.videoId, startSec: start, embedUrl };
  }

  if (!/^https:\/\//i.test(trimmed)) {
    return { kind: "invalid", message: "File URL must start with https://" };
  }
  return { kind: "file", fileUrl: trimmed };
}
